import { useState, useEffect, useMemo } from "react";
import { Breakpoint } from "util/screen";

enum DeviceType {
  mobile = "mobile",
  desktop = "desktop",
}

export const mqMatches = (breakpoint: Breakpoint) => {
  const query = `(min-width: ${breakpoint}px)`;
  const mediaQueryList: MediaQueryList = window.matchMedia(query);

  return Boolean(mediaQueryList.matches);
};

export const useDeviceType = () => {
  const [deviceType, setDeviceType] = useState<DeviceType>();
  const isMobile = useMemo(
    () => deviceType === DeviceType.mobile,
    [deviceType]
  );
  const isDesktop = useMemo(
    () => deviceType === DeviceType.desktop,
    [deviceType]
  );
  useEffect(() => {
    let hasTouchScreen = false;
    if (navigator.maxTouchPoints) {
      hasTouchScreen = navigator.maxTouchPoints > 0;
    } else {
      const mQ = window.matchMedia("(pointer:coarse)");
      if (mQ && mQ.media === "(pointer:coarse)") {
        hasTouchScreen = !!mQ.matches;
      } else if ("orientation" in window) {
        hasTouchScreen = true; // deprecated, but good fallback
      } else {
        // Only as a last resort, fall back to user agent sniffing
        var UA = navigator.userAgent;
        hasTouchScreen =
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
          /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
      }
    }
    if (hasTouchScreen) {
      setDeviceType(DeviceType.mobile);
    } else {
      setDeviceType(DeviceType.desktop);
    }
  }, []);
  return { deviceType, isMobile, isDesktop };
};

export const useMediaQuery = (
  breakpoint: Breakpoint
): [boolean, () => void] => {
  const query = `(min-width: ${breakpoint}px)`;
  const mediaQueryList: MediaQueryList = window.matchMedia(query);

  const [match, setMatch] = useState<boolean>(mediaQueryList.matches);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const onChange = () => {
      setMatch(Boolean(mediaQueryList.matches));
    };

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", onChange);
    } else {
      mediaQueryList.addListener(onChange);
    }

    setMatch(mediaQueryList.matches);

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener("change", onChange);
      } else {
        mediaQueryList.removeListener(onChange);
      }
    };
  }, [breakpoint, mediaQueryList]);

  const refresh = () => {
    setMatch(mediaQueryList.matches);
  };

  return [match, refresh];
};
